export type MediaRecoveryCode =
  | 'DEVICE_UNAVAILABLE'
  | 'PERMISSION_DENIED'
  | 'UNSUPPORTED'
  | 'UNKNOWN'

export type MediaRecoveryFeature = 'AUDIO_RECORDING' | 'SPEECH_RECOGNITION'

export interface MediaRecoveryState {
  readonly code: MediaRecoveryCode
  readonly message: string
}

function getErrorText(error: unknown) {
  if (typeof error === 'string') {
    return error.toLowerCase()
  }

  if (error instanceof Error) {
    return `${error.name} ${error.message}`.toLowerCase()
  }

  return ''
}

function getFeatureLabel(feature: MediaRecoveryFeature) {
  return feature === 'SPEECH_RECOGNITION' ? 'Speech input' : 'Microphone recording'
}

export function normalizeMediaRecoveryError(
  error: unknown,
  feature: MediaRecoveryFeature,
): MediaRecoveryState {
  const errorText = getErrorText(error)
  const featureLabel = getFeatureLabel(feature)

  if (
    errorText.includes('notfounderror') ||
    errorText.includes('requested device not found') ||
    errorText.includes('device unavailable') ||
    errorText.includes('audio-capture') ||
    errorText.includes('no microphone')
  ) {
    return {
      code: 'DEVICE_UNAVAILABLE',
      message: 'No microphone is available. Connect one, then try again.',
    }
  }

  if (
    errorText.includes('notallowederror') ||
    errorText.includes('securityerror') ||
    errorText.includes('permission denied') ||
    errorText.includes('not-allowed') ||
    errorText.includes('service-not-allowed')
  ) {
    return {
      code: 'PERMISSION_DENIED',
      message: 'Microphone access is blocked. Allow access, then try again.',
    }
  }

  if (
    errorText.includes('notsupportederror') ||
    errorText.includes('not supported') ||
    errorText.includes('not implemented')
  ) {
    return {
      code: 'UNSUPPORTED',
      message: `${featureLabel} is not supported in this browser.`,
    }
  }

  return {
    code: 'UNKNOWN',
    message: `We could not start ${featureLabel.toLowerCase()}. Please try again.`,
  }
}
